import { Card } from '@components/card';
import { createForm } from '@components/form';
import { Spinner } from '@components/loading-spinner';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { abbreviateDate } from '@utils/date';

import { IntegrationHeader } from 'components/IntegrationHeader';
import { useStore } from 'models/Provider';

import { TriggerData, ApplicationData, InitialData } from './api';
import { useCreateLearnWorldsUser, useLearnWorldsApplicationData } from './hooks';
import locale from './locale.json';

type FinishedFormTextProps = {
  label: string;
  text?: string;
};

const FinishedFormText = (props: FinishedFormTextProps) => (
  <Stack space={1}>
    <Text font="semibold14" text={props.label} />
    <Text text={props.text} />
  </Stack>
);

type FinishedFormProps = {
  applicationData?: ApplicationData;
};

const FinishedForm = (props: FinishedFormProps) => {
  const { t } = useScopedTranslate(locale);
  const { lang } = useStore();

  return (
    <Card space={6}>
      <Stack space={6}>
        <Stack space={4}>
          <FinishedFormText label={t('name')} text={props.applicationData?.data?.name} />
          <FinishedFormText label={t('ssn')} text={props.applicationData?.data?.ssn} />
          <FinishedFormText label={t('email')} text={props.applicationData?.data?.email} />
          <FinishedFormText
            label={t('firstworkday')}
            text={
              props.applicationData?.data?.firstWorkday
                ? abbreviateDate(props.applicationData.data.firstWorkday, lang, 'date')
                : ''
            }
          />
          <FinishedFormText
            label={t('department')}
            text={(props.applicationData?.data?.department ?? [])
              .map((department) => department.label)
              .join(', ')}
          />
        </Stack>
        <Separator />
        <Stack space={4}>
          <FinishedFormText label={t('createdby')} text={props.applicationData?.sentBy} />
          <FinishedFormText
            label={t('datecreated')}
            text={
              props.applicationData?.created
                ? abbreviateDate(props.applicationData?.created, lang, 'datetime')
                : ''
            }
          />
        </Stack>
      </Stack>
    </Card>
  );
};

type NewFormProps = {
  applicationId: string;
  initialData?: InitialData;
  departmentOptions: { value: string; label: string }[];
};

const Form = createForm<TriggerData>();

const NewForm = (props: NewFormProps) => {
  const { t } = useScopedTranslate(locale);

  const initialData = props.initialData ?? ({} as InitialData);
  const submit = useCreateLearnWorldsUser(props.applicationId);

  return (
    <Card variant="transparent" space={4}>
      <Form
        onSubmit={(data, action) => {
          if (action === 'submit') {
            return submit.mutateAsync(data);
          }
          if (action === 'save') {
            return submit.mutateAsync({ ...data, save: true });
          }
        }}
      >
        <Form.TextField
          id="name"
          label={t('name')}
          validators={['required']}
          defaultValue={initialData.name}
        />
        <Form.TextField
          id="ssn"
          label={t('ssn')}
          validators={['required']}
          defaultValue={initialData.ssn}
        />
        <Form.TextField
          id="email"
          label={t('email')}
          validators={['required']}
          defaultValue={initialData.email}
        />
        <Form.SelectMulti
          id="department"
          placeholder={t('selectdepartments')}
          label={t('department')}
          options={props.departmentOptions}
          defaultValue={
            typeof initialData.department === 'object'
              ? initialData.department
              : props.departmentOptions.find((dep) => dep.value === initialData.department)
          }
          validators={[]}
          noOptions={({ inputValue }) => (inputValue !== '' ? 'No match' : 'No departments found')}
        />
        <Form.DatePicker
          id="firstWorkday"
          label={t('firstworkday')}
          validators={['required']}
          defaultValue={initialData.firstWorkday}
        />
        <Stack space={2} orientation="horizontal" justify="flex-end">
          <Form.Submit action="save" text={t('save')} variant="primary-blue" />
          <Form.Submit text={t('createuser')} />
        </Stack>
      </Form>
    </Card>
  );
};

type LearnWorldsOnboardingProps = {
  applicationId: string;
};

export const LearnWorldsOnboarding = (props: LearnWorldsOnboardingProps) => {
  const { t } = useScopedTranslate(locale);
  const applicationDataQuery = useLearnWorldsApplicationData(props.applicationId);

  if (applicationDataQuery.isLoading) {
    return <Spinner />;
  }
  if (applicationDataQuery.isError || !applicationDataQuery.data) {
    return <div>{t('erroroccurred')}</div>;
  }
  const integration = applicationDataQuery.data;

  return (
    <Stack space={8}>
      <IntegrationHeader
        integration={{
          name: integration.name,
          title: integration.title,
          description: integration.description,
        }}
      />
      {integration.isDone ? (
        <FinishedForm applicationData={integration.applicationData} />
      ) : (
        <NewForm
          applicationId={props.applicationId}
          initialData={integration.integrationData}
          departmentOptions={integration.departmentOptions}
        />
      )}
    </Stack>
  );
};
